import hooks from './hooks';
import IssueReader from './IssueReader/IssueReader';
import IssueReaderPluginApi from './IssueReaderPluginApi/IssueReaderPluginApi';
import imageUtils from './shared/imageUtils';
import urlHashRouting from './shared/urlHashRouting';

/* eslint-disable no-unreachable */

/**
 * This class must be inherited by all Asteikko Mag app implementations
 * (e.g. web app, iOS app, etc.).
 */
export default class AppApi {

  constructor() {
    // Support for addAction(), doAction(), etc.
    // See https://www.npmjs.com/package/@wordpress/hooks
    this.hooks = hooks;
    this.urlHashRouting = urlHashRouting;
    this.issueReader = new IssueReader( this.hooks, this.console, this.issueService, this.i18n );
    this.issueReaderPluginApi = new IssueReaderPluginApi( this );

    this.hooks.addAction( 'app_initialized', 'app_api', () => {
      // Listen for window.location.hash changes
      this.urlHashRouting.listen();
      this.console.log( 'Listening for window.location.hash changes' );
    } );
  }

  /**
   * You will probably want to override this in a sub-class.
   *
   * @return {Promise<AppApi>} This Promise is resolved when the app has fully initialized. The "app_initialized" action is also triggered.
   */
  init() {
    throw new Error( '`init()` must be overridden by the App implementation. init() must return a Promise that triggers the action `app_initialized` when the whole app has been initialized.' );

    return Promise.resolve().then( () => {
      // init() MUST always trigger this action when the app has been fully initialized
      this.hooks.doAction( 'app_initialized' );
      return this;
    } );
  }

  get env() {
    throw new Error( '`get env` must be overridden by the App implementation' );
  }

  get appName() {
    throw new Error( '`get appName` must be overridden by the App implementation' );
  }

  get networkStatus() {
    throw new Error( '`get networkStatus` must be overridden by the App implementation' );
  }

  set networkStatus( isOnline ) {
    throw new Error( '`set networkStatus` must be overridden by the App implementation' );

    const oldStatus = this.networkStatus;

    if ( isOnline !== oldStatus ) {
      this.hooks.doAction( 'network_status_changed', isOnline );
    }
  }

  get issueService() {
    return {
      /**
       * @return {Promise<Object>}
       */
      requestIssue( issueId ) {
        return Promise.reject( '`requestIssue()` must be overridden by the App implementation' );
      },
      /**
       * @return {Promise<String>}
       */
      requestIssueCss( issue ) {
        return Promise.reject( '`requestIssueCss()` must be overridden by the App implementation' );
      },
      /**
       * @return {Promise<String>}
       */
      requestPageHtml( pageIndex, page, issue ) {
        return Promise.reject( '`requestPageHtml()` must be overridden by the App implementation' );
      },
      /**
       * @return undefined
       */
      cancelPageHtmlRequest( pageIndex ) {
        throw new Error( '`cancelPageHtmlRequest()` must be overridden by the App implementation' );
      },
    };
  }

  get i18n() {
    return {
      language: 'en',
      strings: {},
      /**
       * @return {Promise<undefined>}
       */
      setLanguage( language ) {
        return Promise.reject( '`setLanguage()` must be overridden by the App implementation' );
      },
      /**
       * @return {Promise<undefined>}
       */
      restorePreviousLanguage() {
        return Promise.reject( '`restorePreviousLanguage()` must be overridden by the App implementation' );
      },
    };
  }

  get user() {
    return {
      get isLoggedIn() {
        return false;
      },
      set isLoggedIn( newStatus ) {
        const oldStatus = this.isLoggedIn;
        this.isLoggedIn = newStatus;

        if ( oldStatus !== newStatus ) {
          this.hooks.doAction( 'login_status_changed', newStatus );
        }
      },
      isEditor: false,
      isAdmin: false,
    };
  }

  get social() {
    return {
      /**
       * @param {string} socialMediaPlatform
       * @param {int} pageId Defaults to the current page's ID
       */
      sharePage( socialMediaPlatform, pageId = null ) {
        throw new Error( '`sharePage()` must be overridden by the App implementation' );
      }
    };
  }

  get console() {
    return {
      log( ...args ) {
        window.console.log.apply( window.console, args );
      },
      warn( ...args ) {
        window.console.warn.apply( window.console, args );
      },
      error( ...args ) {
        window.console.error.apply( window.console, args );
      },
    };
  }

  get utils() {
    return {
      img: imageUtils,
    };
  }
}

/* eslint-enable no-unreachable */

